"use client";

import { c as _c } from "react/compiler-runtime";
import { motion } from "motion/react";
import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { useChart } from "./chart-context";
export interface BarYAxisProps {
  /** Whether to show all labels or skip some for dense data. Default: true */
  showAllLabels?: boolean;
  /** Maximum number of labels to show. Default: 20 */
  maxLabels?: number;
}
interface BarYAxisLabelProps {
  label: string;
  y: number;
  bandHeight: number;
  isHovered: boolean;
}
function BarYAxisLabel(t0) {
  const $ = _c(16);
  const {
    label,
    y,
    bandHeight,
    isHovered
  } = t0;
  let t1;
  if ($[0] !== bandHeight || $[1] !== y) {
    t1 = {
      top: y,
      height: bandHeight
    };
    $[0] = bandHeight;
    $[1] = y;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const t2 = isHovered ? 1 : 0.7;
  const t3 = isHovered ? "var(--foreground)" : "var(--chart-label, var(--color-zinc-500))";
  let t4;
  if ($[3] !== t2 || $[4] !== t3) {
    t4 = {
      opacity: t2,
      color: t3
    };
    $[3] = t2;
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = cn("truncate whitespace-nowrap text-right text-xs");
    t6 = {
      opacity: 0.7,
      color: "var(--chart-label, var(--color-zinc-500))"
    };
    t7 = {
      maxWidth: 70
    };
    t8 = {
      duration: 0.15
    };
    $[6] = t5;
    $[7] = t6;
    $[8] = t7;
    $[9] = t8;
  } else {
    t5 = $[6];
    t6 = $[7];
    t7 = $[8];
    t8 = $[9];
  }
  let t9;
  if ($[10] !== label || $[11] !== t4) {
    t9 = <motion.span animate={t4} className={t5} initial={t6} style={t7} transition={t8}>{label}</motion.span>;
    $[10] = label;
    $[11] = t4;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t1 || $[14] !== t9) {
    t10 = <div className="absolute right-0 flex items-center justify-end pr-2" style={t1}>{t9}</div>;
    $[13] = t1;
    $[14] = t9;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  return t10;
}
export function BarYAxis({
  showAllLabels = true,
  maxLabels = 20
}: BarYAxisProps) {
  const {
    margin,
    containerRef,
    barScale,
    bandWidth,
    barXAccessor,
    data,
    hoveredBarIndex
  } = useChart();
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(null);

  // Sync portal container (adjust state during render)
  if (containerRef.current && containerRef.current !== portalContainer) {
    setPortalContainer(containerRef.current);
  }

  // Generate labels for each bar
  const labelsToShow = useMemo(() => {
    if (!(barScale && bandWidth && barXAccessor)) {
      return [];
    }
    const allLabels = data.map((d, i) => {
      const label = barXAccessor(d);
      const bandY = barScale(label) ?? 0;
      // Center the label vertically within the band
      const y = bandY + margin.top;
      return {
        label,
        y,
        bandHeight: bandWidth,
        index: i
      };
    });

    // If showAllLabels is true or we have fewer than maxLabels, show all
    if (showAllLabels || allLabels.length <= maxLabels) {
      return allLabels;
    }

    // Otherwise, skip some labels to avoid crowding
    const step = Math.ceil(allLabels.length / maxLabels);
    return allLabels.filter((_, i_0) => i_0 % step === 0);
  }, [barScale, bandWidth, barXAccessor, data, margin.top, showAllLabels, maxLabels]);

  // Use portal to render into the chart container
  if (!portalContainer) {
    return null;
  }

  // Early return if not in a BarChart
  if (!barScale) {
    return null;
  }
  return createPortal(<div className="pointer-events-none absolute bottom-0 top-0" style={{
    left: 0,
    width: margin.left
  }}>
      {labelsToShow.map(item => <BarYAxisLabel bandHeight={item.bandHeight} isHovered={hoveredBarIndex === item.index} key={`${item.label}-${item.y}`} label={item.label} y={item.y} />)}
    </div>, portalContainer);
}
BarYAxis.displayName = "BarYAxis";
export default BarYAxis;